import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { navigate } from 'gatsby';
import { Button, Flex, Heading, Text as T } from '@rebass/emotion';
import CmsPage from '../components/Elements/CmsPage';
import { PageProvider } from '../context/PageProvider';
import React, { useState, useEffect } from 'react';
import Text from '~components/Elements/Text';
import Header from '~components/PageBlocks/Header/index';
import PathwayOverview from '~components/PageBlocks/PathwayOverview';
import PathwayResume from '~components/PageBlocks/PathwayResume';
import Root from '~components/Root';
import { responseObj } from '~lib/pathwayQs';
import { useLocalStorage } from '../hooks/useLocalStorage';
import Close from '~icons/close';
import ArrowRight from '~icons/arrow-right';
import ArrowLeft from '~icons/arrow-left';

const App = styled(Flex)`
  flex-direction: column;
  justify-content: stretch;
  align-items: stretch;
  min-height: 100vh;
  padding: 0%;
  max-width: 100vw;
  height: 100%;
  width: 100%;
  font-family: ${props => props.theme.fonts.sans};
`;

const Main = styled(Flex)`
  flex: 1 0 auto;
  justify-content: center;
  align-items: center;
  flex-direction: column;
  height: 100%;
  padding-bottom: 91px;
`;

const WhiteBg = styled(Flex)`
  height: 100%;
  justify-content: center;
  flex-direction: column;
  align-items: center;
  width: auto;
  background-color: ${props => props.theme.colors.neutralNeutral0};
`;

const ColourBg = styled(Flex)`
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
  flex: 1;
  background-image: radial-gradient(
      circle at 0 0,
      ${props => props.theme.colors.blueBlue20},
      rgba(255, 255, 255, 0)
    ),
    linear-gradient(
      to top,
      ${props => props.theme.colors.blueBlue20},
      ${props => props.theme.colors.blueBlue20},
      ${props => props.theme.colors.blueBlue40}
    );
`;

const HeroBoxColumn = styled(Flex)`
  flex-direction: column;
  align-items: center;

  gap: 2vh;
  text-align: center;
`;

const BodyButtonRow = styled(Flex)`
  justify-content: center;
  display: flex;
  flex-wrap: wrap;
  gap: 20px;
  max-width: 990px;
  margin-top: 10px;

  @media (max-width: ${props => props.theme.device.md}) {
    width: 100%;
    flex-direction: column;
    justify-content: start;
  }
`;

const FooterBlock = styled(Flex)`
  position: fixed;
  bottom: 0;
  left: 0;
  right: 0;
  padding: 2vh 5vw;
  border-top: 1px solid #33333320;
  justify-content: space-between;
  flex-direction: row;
  width: 100%;
  background: #fff;

  @media (max-width: ${props => props.theme.device.md}) {
    background: #fff;
    position: fixed;
    bottom: 0;
    left: 0;
  }
`;

const H1 = styled(Heading)`
  ${props => props.theme.fonts.FeatureL}
  -webkit-background-clip: text;
  background-clip: text;
  -webkit-text-fill-color: transparent;
  background-color: ${props => props.theme.colors.blueBlue160};

  @media (max-width: ${props => props.theme.device.md}) {
    font-size: 36px;
    line-height: 44px;
  }
`;

const PathwayContentButton = ({ children, ...rest }) => {
  const BodyBtn = styled(Button)`
    ${props => props.theme.fonts.Body}
    height: 64px;
    background-color: ${props =>
      props.active
        ? props.theme.colors.orangeOrange120
        : props.theme.colors.neutralNeutral0};
    color: ${props => props.theme.colors.neutralNeutral180};
    border-radius: 32px;
    font-weight: ${props => (props.active ? 'bold' : 'normal')};
    @media (max-width: ${props => props.theme.device.lg}) {
      width: 90%;
      margin: 0 auto;
    }
  `;

  return (
    <BodyBtn className="BodyBtn" {...rest}>
      {' '}
      <T mx={20}>{children}</T>{' '}
    </BodyBtn>
  );
};

const PathwayFooterButton = ({
  back,
  exit,
  next,
  active,
  inactive,
  neutral,
  ...rest
}) => {
  const FooterBtn = styled(Button)`
    height: 48px;
    color: ${props => props.theme.colors.neutralNeutral180};
    border-radius: 24px;

    @media (max-width: ${props => props.theme.device.sm}) {
      #nav-text {
        display: none;
      }
      padding-left: 24px;
      padding-right: 24px;
    }
  `;

  const theme = useTheme();
  const bg = active
    ? 'orangeOrange120'
    : inactive
    ? 'neutralNeutral30'
    : neutral
    ? 'neutralNeutral0'
    : '';

  const border =
    active || neutral ? 'orangeOrange120' : inactive ? 'neutralNeutral30' : '';

  const textColour =
    active || neutral
      ? 'neutralNeutral180'
      : inactive
      ? 'neutralNeutral100'
      : '';

  return back ? (
    <FooterBtn
      {...rest}
      bg={bg}
      style={{ border: `1px solid ${theme.colors[border]}` }}
    >
      <Flex style={{ justifyContent: 'space-around' }}>
        <ArrowLeft
          height={16}
          width={16}
          color={theme.colors.neutralNeutral180}
        />
        <T
          id="nav-text"
          ml={10}
          style={{ display: { sm: 'none' }, color: theme.colors[textColour] }}
        >
          {'Back'}
        </T>
      </Flex>
    </FooterBtn>
  ) : exit ? (
    <FooterBtn
      {...rest}
      bg={bg}
      style={{ border: `1px solid ${theme.colors[border]}` }}
    >
      <Flex style={{ justifyContent: 'space-around' }}>
        <Close height={16} width={16} color={theme.colors.neutralNeutral180} />
        <T
          id="nav-text"
          ml={10}
          style={{ display: { sm: 'none' }, color: theme.colors[textColour] }}
        >
          {'Exit'}
        </T>
      </Flex>
    </FooterBtn>
  ) : next ? (
    <FooterBtn
      {...rest}
      bg={bg}
      style={{ border: `1px solid ${theme.colors[border]}` }}
    >
      <Flex style={{ justifyContent: 'space-around' }}>
        <T
          id="nav-text"
          mr={10}
          style={{ display: { sm: 'none' }, color: theme.colors[textColour] }}
        >
          {'Next'}
        </T>
        <ArrowRight
          height={16}
          width={16}
          color={theme.colors.neutralNeutral180}
        />
      </Flex>
    </FooterBtn>
  ) : null;
};

const PathwayQuestionnaire = ({ pageContext }) => {
  const [option, setOption] = useState('initial');
  const [selectedOption, setSelectedOption] = useState(null);
  const [pathwayArr, setPathwayArr] = useState([{}]);
  const [isCompleted, setIsCompleted] = useState(null);

  const [pathwayStage, setPathwayStage] = useLocalStorage(
    'pathway-stage',
    null
  );
  const [pathwaySection, setPathwaySection] = useLocalStorage(
    'pathway-section',
    null
  );

  // useEffect(() => {
  //   if (pathwayStage && pathwaySection) {
  //     const menu = pageContext.pathwayMenus.find(
  //       m => m.section === pathwaySection
  //     );
  //     const page = menu.items[pathwayStage - 1].items[0].page;
  //     const to = `/pathway/${pathwaySection}/${page.topic.slug.current}/${page.slug.current}`;
  //     navigate(to);
  //   }
  // }, [pathwayStage, pathwaySection]);

  const initialPage = () => {
    const onExitClick = () => {
      navigate('/');
    };

    const onNextClick = () => {
      setPathwayArr(pathwayArr.concat(responseObj));
      setOption('default');
    };

    return (
      <CmsPage pageContext={pageContext}>
        <PageProvider page={pageContext}>
          <Main>
            <WhiteBg>
              <HeroBoxColumn
                pb={['70px', '70px', '70px', 0]}
                width="100%"
                px={['20px', '20px', '20px', 0]}
              >
                <H1 as="h1">Tell us about yourself</H1>
                <Text font={'Body'} color={'neutralNeutral180'}>
                  This short questionnaire should only take 2-3 minutes and will
                  help us select the right pathway for you and the best place to
                  begin.
                </Text>
              </HeroBoxColumn>
            </WhiteBg>
            <FooterBlock>
              <div style={{ width: '66px', height: '48px' }} />
              <PathwayFooterButton exit onClick={onExitClick} neutral />
              <PathwayFooterButton next onClick={onNextClick} active />
            </FooterBlock>
          </Main>
        </PageProvider>
      </CmsPage>
    );
  };

  const defaultPage = () => {
    pathwayArr[pathwayArr.length - 1].stage &&
      setIsCompleted(pathwayArr[pathwayArr.length - 1].stage);

    const onOptionClick = (e, item) => {
      // e.currentTarget.style.border = `1px solid #091e42`;
      setSelectedOption(item);
      // e.currentTarget.style.border = `1px solid #091e42`;
    };

    const onNextClick = () => {
      if (selectedOption) {
        let concatenatedArr = pathwayArr.concat(selectedOption);
        setPathwayArr(concatenatedArr);
        setOption(selectedOption);
        setSelectedOption(null);
      }
    };

    const onBackClick = () => {
      pathwayArr.pop();
      setOption(pathwayArr[pathwayArr.length - 1].option);
      setPathwayArr(pathwayArr);
    };

    const onExitClick = () => {
      navigate('/');
    };

    return (
      <CmsPage pageContext={pageContext}>
        <PageProvider page={pageContext}>
          <Main>
            <ColourBg>
              <HeroBoxColumn
                pb={['90px', '90px', '90px', 0]}
                width="100%"
                px={['20px', '20px', '20px', 0]}
              >
                <Text font={'HeadingM'} color={'neutralNeutral180'}>
                  {pathwayArr[pathwayArr.length - 1].question}
                </Text>
                <Text
                  fontSize={['28px', '28px', '28px', '36px']}
                  lineHeight={['36px', '36px', '36px', '44px']}
                  fontWeight="400"
                  color={'neutralNeutral180'}
                >
                  {pathwayArr[pathwayArr.length - 1].subQuestion}
                </Text>
                <BodyButtonRow>
                  {pathwayArr[pathwayArr.length - 1].responses &&
                    pathwayArr[pathwayArr.length - 1].responses.map(item => {
                      return (
                        <PathwayContentButton
                          item
                          active={selectedOption === item}
                          onClick={e => onOptionClick(e, item)}
                        >
                          {item.option}
                        </PathwayContentButton>
                      );
                    })}
                </BodyButtonRow>
              </HeroBoxColumn>
            </ColourBg>
            <FooterBlock>
              <PathwayFooterButton back onClick={onBackClick} neutral />
              <PathwayFooterButton exit onClick={onExitClick} neutral />
              <PathwayFooterButton
                next
                onClick={onNextClick}
                active={selectedOption ? true : false}
              />
            </FooterBlock>
          </Main>
        </PageProvider>
      </CmsPage>
    );
  };

  if (pathwayStage && pathwaySection) {
    return (
      <CmsPage pageContext={pageContext}>
        <PageProvider page={pageContext}>
          <App>
            <header>
              <Header data={pageContext.header}></Header>
            </header>
            <PathwayResume pageContext={pageContext} />
          </App>
        </PageProvider>
      </CmsPage>
    );
  }

  if (isCompleted) {
    return (
      <CmsPage pageContext={pageContext}>
        <PageProvider page={pageContext}>
          <PathwayOverview pageContext={pageContext} option={option} />
        </PageProvider>
      </CmsPage>
    );
  }

  return (
    <CmsPage pageContext={pageContext}>
      <PageProvider page={pageContext}>
        <App>
          <header>
            <Header data={pageContext.header}></Header>
          </header>
          {pathwayArr.length === 1 ? initialPage() : defaultPage()}
        </App>
      </PageProvider>
    </CmsPage>
  );
};

export default PathwayQuestionnaire;
